import { AfterViewInit, ChangeDetectorRef, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, fromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, share } from 'rxjs/operators';
import { I18nTranslator } from '../../../../i18n/translate';
import { AROrderItem, ARResult } from '../../../../models/agrorendement-common.model';
import { Campaign } from '../../../../models/campaign.model';
import { ListItem } from '../../../../models/lists.model';
import { Organisation } from '../../../../models/organisation.model';
import { Page } from '../../../../models/page.model';
import { User } from '../../../../models/user.model';
import { ARResultFilter, ARResultService } from '../../../../services/agrorendement-common.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { ModalService } from '../../../../services/modal.service';
import { OrganisationService } from '../../../../services/organisation.service';
import { UserService } from '../../../../services/user.service';
import { TOOLTIP_TEXTS } from '../../../../utils/tooltip-texts';
import { currentCampaign, showErrorMessage, stob, toNull, toNumber } from '../../../../utils/utils';
import { ARResultsNotifyDialogComponent, ARResultsNotifyDialogContext } from './results-notify-dialog.component';

// FIXME modifier le schema et les classes pour avoir une seule classe Result commune a toutes les cultures

// FIXME sortir ca de AR et mettre en commun avec tous les outils (y compris ZSN)

export abstract class ARResultsComponent<OI extends AROrderItem<any, any>, C extends Campaign, RT extends ListItem>
  implements OnInit, AfterViewInit, OnDestroy
{
  sub: any;
  error: any;
  termPrefixFilterParcel: ElementRef;
  termPrefixFilterExploitation: ElementRef;
  termCampaignFilter: ElementRef;
  termResultTypeFilter: ElementRef;
  termOrganisationFilter: ElementRef;
  termValidationStatusFilter: ElementRef;
  termAvailabilityStatusFilter: ElementRef;
  termNotificationStatusFilter: ElementRef;
  termNotificationAdvisorsStatusFilter: ElementRef;
  termPrefixFilterIdParcel: ElementRef;
  allFilters: ElementRef;
  termAdvisorFilter: ElementRef;

  filterParcel: string;
  filterExploitation: string;
  filterIdParcel: string;

  prefixFilterParcel: string = '';
  prefixFilterExploitation: string = '';
  prefixFilterIdParcel: number = null;
  campaignFilter: number = null;
  organisationFilter: number = null;
  resultTypeFilter: number = null;
  validationStatusFilter: boolean = null;
  availabilityStatusFilter: boolean = null;
  notificationStatusFilter: boolean = null;
  notificationAdvisorsStatusFilter: boolean = null;
  advisorFilter: number | string = null;

  results: ARResult<OI>[] = [];
  page: number = 1;
  itemsPerPage: number = 25;
  maxSize: number = 5;
  numPages: number = 1;
  length: number = 0;

  public advisors: Array<User> = [];
  organisations: Organisation[];
  campaigns: C[];
  resultTypes: RT[];
  resultTypesDict: Map<string, RT>;

  filterToggleIcon = 'fa fa-plus';
  showAllFilters = false;

  isViewInit = true;
  // montrer ou pas les composants pour valider ou notifier les utilisateurs
  protected showLifecycleUpdates = false;
  protected isAdminOrSector = false;
  protected isAdmin = false;
  protected isNegoce = false;
  protected isManager = false;
  protected isWanakaUser = false;

  // injecte par le constructeur de la sous-classe concrete
  protected i18nTranslator: I18nTranslator;
  protected service: ARResultService<OI, C>;
  protected organisationService: OrganisationService;
  protected router: Router;
  protected route: ActivatedRoute;
  protected modal: ModalService;
  protected ref: ChangeDetectorRef;
  protected userService: UserService;

  // a redefinir dans les sous classes
  specyUrlPart: string[];
  specyString: string;
  campaignReport: boolean;
  isGenerateConsole = false;

  // gestion des checkboxes
  // injection ViewChild dans classe concrete
  resultCheckboxes: any;
  checkboxAll: any;
  lengthAllResults: number;
  checkedResultIds = new Set<number>();

  tooltipTexts = TOOLTIP_TEXTS;

  // a overrider
  abstract isCampaignCurrentlyOpen(campaign: C): boolean;
  abstract getResultTypes(): Observable<Map<any, ListItem[]>>;
  navigateToGenerateConsole(): any {
    // do nothing
  }

  constructor() {}

  handlePage(page: Page<ARResult<OI>>) {
    this.results = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  ngOnInit() {
    if (sessionStorage.getItem(this.specyUrlPart[1] + 'Filter')) {
      const oldFilter = JSON.parse(sessionStorage.getItem(this.specyUrlPart[1] + 'Filter'));
      this.toggleAllFilters();
      for (const key in oldFilter) {
        if (oldFilter[key] != undefined) {
          this.prefixFilterIdParcel = oldFilter['prefixFilterIdParcel'];
          this.prefixFilterExploitation = oldFilter['prefixFilterExploitation'];
          this.prefixFilterParcel = oldFilter['prefixFilterParcel'];
          this.campaignFilter = oldFilter['campaignFilter'];
          this.organisationFilter = oldFilter['organisationFilter'];
          this.resultTypeFilter = oldFilter['resultTypeFilter'];
          this.validationStatusFilter = oldFilter['validationStatusFilter'];
          this.availabilityStatusFilter = oldFilter['availabilityStatusFilter'];
          this.notificationStatusFilter = oldFilter['notificationStatusFilter'];
          this.notificationAdvisorsStatusFilter = oldFilter['notificationAdvisorsStatusFilter'];
          this.advisorFilter = oldFilter['advisorFilter'];
          // si bug sur le site retirer ca
          //eval('"use strict"; this.'+ key + '=' + (typeof(oldFilter[key]) == 'string' ? JSON.stringify(oldFilter[key]) : oldFilter[key]))
        }
      }
      this.filterParcel = this.prefixFilterParcel;
      this.filterExploitation = this.prefixFilterExploitation;
      this.filterIdParcel = this.prefixFilterIdParcel ? this.prefixFilterIdParcel.toString() : null;
      sessionStorage.removeItem(this.specyUrlPart[1] + 'Filter');
    }
    const isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    this.isAdmin = isAdmin;
    const isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    this.isNegoce = isNegoce;
    const isManager = KeycloakService.auth.authz.hasRealmRole('ROLE_MANAGER');
    this.isManager = isManager;
    const isSector = KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    if (isAdmin || isSector) {
      this.isAdminOrSector = true;
      this.showLifecycleUpdates = true;
      this.isWanakaUser = true;
      // 10 items par page pour les admins dans le menu Mes conseils (25 pour les autres roles)
      this.itemsPerPage = 10;
      this.getOrganisations();
    } else {
      this.isAdminOrSector = false;
      // rest de l'init faite dans  ngAfterViewInit
    }

    // besoin des types pour la table
    const obs1 = this.getResultTypes();
    // pour le filtre initial
    const obs2 = this.getCampaigns();
    forkJoin([obs1, obs2]).subscribe((data: Array<any>) => {
      this.campaignFilter = this.campaigns[0].id;
      this.onChangeTable();
    });
  }

  ngAfterViewInit() {
    if (!this.isAdminOrSector) {
      // les filtres ont besoin de savoir showLifecycleUpdates
      // TODO faire ca dans ngInit => besoin de savoir quand reponse est recue
      this.service.canPerfomLifecycleUpdates().subscribe((x) => {
        this.showLifecycleUpdates = x;
        this.isWanakaUser = false;
        if (x) {
          this.getOrganisations();
        }
        // showLifecycleUpdates est utilise pour creer ou non des controles UI (ngIf)
        // les filtres ont besoin de ces controles => setTimeout defer l'execution
        // apres que ce binding a ete processed
        setTimeout(() => {
          this.setupFilters();
        });
      });
    } else {
      this.setupFilters();
    }
  }

  ngOnDestroy() {}

  setupFilters() {
    // filtres ont besoin du showLifecycleUpdates
    // TODO ameliorer => pas de debounce quand clearFilter a ete appele
    const observables = new Array<Observable<{}>>();
    const obs1 = fromEvent(this.termPrefixFilterParcel.nativeElement, 'keyup').pipe(
      debounceTime(300),
      distinctUntilChanged(),
    );
    const obs2 = fromEvent(this.termPrefixFilterExploitation.nativeElement, 'keyup').pipe(
      debounceTime(300),
      distinctUntilChanged(),
    );
    const obs3 = fromEvent(this.termCampaignFilter.nativeElement, 'change');
    const obs9 = fromEvent(this.termResultTypeFilter.nativeElement, 'change');
    observables.push(obs1, obs2, obs3, obs9);

    if (this.showLifecycleUpdates) {
      const obs5 = fromEvent(this.termAvailabilityStatusFilter.nativeElement, 'change');
      const obs7 = fromEvent(this.termNotificationStatusFilter.nativeElement, 'change');

      if (this.termNotificationAdvisorsStatusFilter) {
        const obs9 = fromEvent(this.termNotificationAdvisorsStatusFilter.nativeElement, 'change');
        observables.push(obs5, obs7, obs9);
      } else {
        observables.push(obs5, obs7);
      }
    }

    if (this.isAdminOrSector) {
      const obs8 = fromEvent(this.termOrganisationFilter.nativeElement, 'change');
      const obs4 = fromEvent(this.termValidationStatusFilter.nativeElement, 'change');
      observables.push(obs4, obs8);
    }
    if (this.isAdmin || this.isNegoce || this.isManager) {
      const obs8 = fromEvent(this.termAdvisorFilter.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs8);
      obs8.subscribe((term) => (this.advisorFilter = <string>this.termAdvisorFilter.nativeElement.value));
    }
    if (this.isAdmin) {
      const obs10 = fromEvent(this.termPrefixFilterIdParcel.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs10);
      obs10.subscribe((term) => {
        this.prefixFilterIdParcel = <number>this.termPrefixFilterIdParcel.nativeElement.value;
        console.log('r');
      });
    }
    obs1.subscribe((term) => (this.prefixFilterParcel = <string>this.termPrefixFilterParcel.nativeElement.value));
    obs2.subscribe(
      (term) => (this.prefixFilterExploitation = <string>this.termPrefixFilterExploitation.nativeElement.value),
    );
    merge(...observables)
      .pipe(debounceTime(10))
      .subscribe((term: any) => {
        this.updateFilters();
      });
  }

  getCampaigns() {
    const obs = this.service.getCampaigns().pipe(share());
    obs.subscribe((campaigns) => {
      this.campaigns = campaigns.sort(Campaign.compare).reverse();
    });
    return obs;
  }

  getOrganisations() {
    this.organisationService.getOrganisations().subscribe((orgs) => {
      this.organisations = orgs.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  clearFilters() {
    this.campaignFilter = currentCampaign(this.campaigns);

    this.prefixFilterParcel = null;
    this.prefixFilterExploitation = null;
    this.resultTypeFilter = null;
    this.filterParcel = null;
    this.filterExploitation = null;

    this.termPrefixFilterParcel.nativeElement.value = null;
    this.termPrefixFilterExploitation.nativeElement.value = null;
    this.termResultTypeFilter.nativeElement.value = null;

    if (this.showLifecycleUpdates) {
      this.availabilityStatusFilter = null;
      this.notificationStatusFilter = null;
      this.notificationAdvisorsStatusFilter = null;

      this.termAvailabilityStatusFilter.nativeElement.value = null;
      this.termNotificationStatusFilter.nativeElement.value = null;
      if (this.termNotificationAdvisorsStatusFilter) {
        this.termNotificationAdvisorsStatusFilter.nativeElement.value = null;
      }
    }

    if (this.isAdminOrSector) {
      this.termOrganisationFilter.nativeElement.value = null;
      this.termValidationStatusFilter.nativeElement.value = null;
      this.validationStatusFilter = null;
      this.organisationFilter = null;
    }
    if (this.isAdmin) {
      this.termPrefixFilterIdParcel.nativeElement.value = null;
      this.prefixFilterIdParcel = null;
      this.filterIdParcel = null;
    }
    if (this.isAdmin || this.isNegoce || this.isManager) {
      this.termAdvisorFilter.nativeElement.value = null;
      this.advisorFilter = null;
    }

    this.updateFilters();
  }

  updateFilters() {
    // reset la page a la premiere et update la table
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du controle de pagination va appeler onChangeTable
      this.page = 1;
    }

    this.clearCheckboxes();
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    const filter = this.buildFilter();
    this.getResultsPaginated(pageNumber, 'processedOn', 'desc', filter);
  }

  onChangeItemsPerPage(newValue: number) {
    const page = this.page;
    this.ref.detectChanges();
    if (page === this.page) {
      this.onChangeTable();
    }
  }

  buildFilter() {
    let notificationAdvisorsStatus: boolean = null;
    if (this.notificationAdvisorsStatusFilter) {
      notificationAdvisorsStatus = stob('' + this.notificationAdvisorsStatusFilter);
    }
    const filter = {
      prefixParcel: this.prefixFilterParcel,
      prefixExploitation: this.prefixFilterExploitation,
      farm: <any>null,
      campaign: toNull(this.campaignFilter),
      organisation: toNull(this.organisationFilter),
      resultType: toNull(this.resultTypeFilter),
      // possible change en string par le binding Angular 2
      validationStatus: stob('' + this.validationStatusFilter),
      notificationStatus: stob('' + this.notificationStatusFilter),
      notificationAdvisorsStatus: notificationAdvisorsStatus,
      availabilityStatus: stob('' + this.availabilityStatusFilter),
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),
      advisorId: toNull(this.advisorFilter),
    };
    return filter;
  }

  getResultsPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: ARResultFilter) {
    this.service
      .getPageResults(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter)
      .subscribe((page) => this.handlePage(page));
  }

  validate(result: ARResult<OI>, isValidated: boolean) {
    const oldValue = result.isValidated;
    result.isValidated = isValidated;
    this.service.validateBatch([result.id], isValidated).subscribe(
      (x) => {},
      (error) => {
        // revert
        result.isValidated = oldValue;
        let msg = '';
        if (oldValue) {
          msg = this.i18nTranslator.translate('ar.result.table.error.invalider');
        } else {
          msg = this.i18nTranslator.translate('ar.result.table.error.valider');
        }
        this.handleError(error, msg);
      },
    );
  }

  makeAvailable(result: ARResult<OI>, isMadeAvailable: boolean) {
    const oldValue = result.isMadeAvailable;
    result.isMadeAvailable = isMadeAvailable;
    this.service.makeAvailableBatch([result.id], isMadeAvailable).subscribe(
      (x) => {},
      (error) => {
        // revert
        result.isMadeAvailable = oldValue;
        let msg = '';
        if (oldValue) {
          msg = this.i18nTranslator.translate('ar.result.table.error.cacher');
        } else {
          msg = this.i18nTranslator.translate('ar.result.table.error.public');
        }
        this.handleError(error, msg);
      },
    );
  }

  invalidateNotification(result: ARResult<OI>) {
    this.modal.confirm(this.i18nTranslator.translate('ar.result.table.confirm.invaliderEnvoi')).then((ok: boolean) => {
      if (ok) {
        result.isNotified = false;
        this.service.invalidateNoficationBatch([result.id]).subscribe(
          (x) => {},
          (error) => {
            // revert
            result.isNotified = true;
            const msg = this.i18nTranslator.translate('ar.result.table.error.invaliderEnvoi');
            this.handleError(error, msg);
          },
        );
      }
    });
  }

  invalidateNotificationAdvisors(result: ARResult<OI>) {
    this.modal
      .confirm(this.i18nTranslator.translate('ar.result.table.confirm.invaliderEnvoi.conseillers'))
      .then((ok: boolean) => {
        if (ok) {
          result.isNotifiedAdvisors = false;
          this.service.invalidateNoficationAdvisorsBatch([result.id]).subscribe(
            (x) => {},
            (error) => {
              // revert
              result.isNotifiedAdvisors = true;
              const msg = this.i18nTranslator.translate('ar.result.table.error.invaliderEnvoi.conseillers');
              this.handleError(error, msg);
            },
          );
        }
      });
  }

  deleteResult(result: ARResult<OI>) {
    this.modal.confirm(this.i18nTranslator.translate('ar.result.table.confirm.suppr')).then((ok: boolean) => {
      if (!ok) {
        return;
      }
      this.service.deleteResult(result).subscribe(
        (x) => {
          this.onChangeTable();
        },
        (error) => {
          this.handleError(error, this.i18nTranslator.translate('ar.result.table.error.suppr'));
        },
      );
    });
  }

  download(result: ARResult<OI>) {
    this.service.downloadResult(result).subscribe(
      (x) => {
        window.location.assign(x.downloadUrl);
      },
      (error) => {
        const msg = this.i18nTranslator.translate('ar.result.table.error.telecharger');
        this.handleError(error, msg);
      },
    );
  }

  downloadAllChecked() {
    this.service.downloadResultsBatch(Array.from(this.checkedResultIds));
  }

  makeAvailableToUsersAllChecked() {
    this.modal.confirm(this.i18nTranslator.translate('ar.result.table.confirm.disponible')).then((ok: boolean) => {
      if (ok) {
        this.service.makeAvailableBatch(Array.from(this.checkedResultIds)).subscribe(
          (x) => {
            this.onChangeTable();
          },
          (error) => {
            this.handleError(error, this.i18nTranslator.translate('ar.result.table.error.disponible'));
          },
        );
      }
    });
  }

  makeValidatedToUsersAllChecked() {
    this.modal.confirm(this.i18nTranslator.translate('ar.result.table.confirm.valider')).then((ok: boolean) => {
      if (ok) {
        this.service.validateBatch(Array.from(this.checkedResultIds)).subscribe(
          (x) => {
            this.onChangeTable();
          },
          (error) => {
            this.handleError(error, this.i18nTranslator.translate('ar.result.table.error.valider.plural'));
          },
        );
      }
    });
  }
  notifyUsersAllChecked() {
    const context = new ARResultsNotifyDialogContext();
    context.isWanakaUser = this.isWanakaUser;
    context.isBatchDialog = true;
    context.showClose = true;
    context.isBlocking = true;
    context.size = 'lg';
    this.modal.open(ARResultsNotifyDialogComponent, context).then((toSend: Array<boolean>) => {
      if (toSend === null || toSend === undefined) {
        // utilisateur a choisi annuler
        return;
      }
      this.service.notifyUsersBatch(Array.from(this.checkedResultIds), toSend).subscribe(
        (res) => {
          this.onChangeTable();
          this.handleEmailsSentResponse(res);
        },
        (error) => {
          if (this.handleErrorNoResultFound(error)) {
            return;
          }
          this.handleError(error, this.i18nTranslator.translate('ar.result.table.error.envoi'));
        },
      );
    });
  }

  notifyUsersSingle(resultId: number, isFillSendFarmers: boolean) {
    const context = new ARResultsNotifyDialogContext();
    context.isWanakaUser = this.isWanakaUser;
    context.isFillSendFarmers = isFillSendFarmers;
    context.isBatchDialog = false;
    context.showClose = true;
    context.isBlocking = true;
    context.size = 'lg';
    this.modal.open(ARResultsNotifyDialogComponent, context).then((toSend: Array<boolean>) => {
      if (toSend === null || toSend === undefined) {
        // utilisateur a choisi annuler
        return;
      }
      this.service.notifyUsers(resultId, toSend).subscribe(
        (res) => {
          this.onChangeTable();
          this.handleEmailsSentResponse(res);
        },
        (error) => {
          if (this.handleErrorNoResultFound(error)) {
            return;
          }
          this.handleError(error, this.i18nTranslator.translate('ar.result.table.error.envoi'));
        },
      );
    });
  }

  handleEmailsSentResponse(res: any) {
    // struct ResultNotificationResult (voir serveur)
    const warnings = res.warnings;
    let msg = '';
    if (warnings && warnings.length > 0) {
      // il ne peut y avoir qu'un seul warning
      // code some_result_not_notified
      const warning = warnings[0];
      const numResultsFound = warning.details.num_results_found;
      msg =
        this.i18nTranslator.translate('ar.result.table.warning.envoi', numResultsFound) +
        this.i18nTranslator.translate('ar.result.table.warning.notSent');
    } else {
      msg = this.i18nTranslator.translate('ar.result.table.envoi.success');
    }

    this.modal.alert(msg);
  }

  handleErrorNoResultFound(error: any) {
    if (error.errorCode == 'invalid') {
      // TODO revoir la structure de la ValidationException
      const data = error.data;
      // il n'y a qu'une erreur de validation possible ici donc on regarde
      if (data && data.hasOwnProperty('no_result_found')) {
        const msg =
          this.i18nTranslator.translate('ar.result.table.error.envoi.none') +
          this.i18nTranslator.translate('ar.result.table.warning.notSent');
        this.modal.alert(msg);
        return true;
      }
    }
    // normalement ne devrait pas arriver
    return false;
  }

  // FIXME more specific
  handleError(error: any, msg: string) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.result.table.error.unauthorized'));
    } else {
      console.error(error);

      // generic error message
      showErrorMessage(this.modal, msg);
    }
  }

  isBatchOperationDisabled(): boolean {
    return this.checkedResultIds.size === 0;
  }

  isResultChecked(result: ARResult<OI>): boolean {
    return this.checkedResultIds.has(result.id);
  }

  onCheckboxClicked(index: number) {
    const checkboxes = this.resultCheckboxes.toArray().map((x: any) => x.nativeElement);
    const result = this.results[index];
    const checkbox = checkboxes[index];
    if (checkbox.checked) {
      this.checkedResultIds.add(result.id);
    } else {
      this.checkedResultIds.delete(result.id);
    }
  }

  onCheckboxAllClicked() {
    if (this.checkboxAll.nativeElement.checked) {
      const filter = this.buildFilter();
      this.service.getResultIds(filter).subscribe(
        (x) => {
          this.lengthAllResults = x.length;

          for (const idx of x) {
            this.checkedResultIds.add(idx);
          }
          // pas de modele : on coche a la main
          const checkboxes = this.resultCheckboxes.toArray().map((x: any) => x.nativeElement);
          for (let i = 0; i < checkboxes.length; i++) {
            const checkbox = checkboxes[i];
            const result = this.results[i];
            checkbox.checked = this.isResultChecked(result);
          }
        },
        (error) => {
          this.handleError(error, this.i18nTranslator.translate('ar.result.table.error.select'));
        },
      );
    } else {
      this.clearCheckboxes();
    }
  }

  clearCheckboxes() {
    this.lengthAllResults = undefined;
    this.checkboxAll.nativeElement.checked = false;
    // decoche tout
    this.checkedResultIds.clear();
    // pas de modele : on decoche a la main
    const checkboxes = this.resultCheckboxes.toArray().map((x: any) => x.nativeElement);
    for (const checkbox of checkboxes) {
      checkbox.checked = false;
    }
  }

  isCheckboxAllChecked(): boolean {
    return this.lengthAllResults === this.checkedResultIds.size;
  }

  toggleAllFilters() {
    if (this.showAllFilters) {
      this.showAllFilters = false;
      this.filterToggleIcon = 'fa fa-plus';
      this.allFilters.nativeElement.style.display = 'none';
    } else {
      this.showAllFilters = true;
      this.filterToggleIcon = 'fa fa-minus';
      this.allFilters.nativeElement.style.display = 'block';
    }
  }
}
